import { ListingHeader } from "../../components/Header";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useConfig } from "../../hooks/useConfig";
import { Alert } from "@mui/material";
import { getIdTokenJwt } from "../../auth/session";
import LoadingSpinner from "../../components/Loading";
import { useParams } from "react-router-dom";

// URL expiration time in milliseconds (15 minutes)
const URL_EXPIRATION_TIME = 15 * 60 * 1000;

const MetaBaseDashboard = () => {
  const metbaseReportConfig = useConfig("metabase_reports")
  const [resp,setResp] = useState<any>({})
  // A failed report fetch used to leave the spinner up forever: the token read
  // (Amplify's Auth.currentSession, Cognito-only) rejected on a GCIP tenant
  // before the request was ever made, and nothing caught it. Now any failure
  // lands here and renders, instead of hanging.
  const [error,setError] = useState<string | null>(null)
  const {id} = useParams();
  const urlTimestampRef = useRef<number>(0);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(()=>{
    let active = true
    
  const metaBaseReport = async()=>{
    // Provider-agnostic: Cognito and GCIP tenants both answer here.
    let token: string;
    try {
      token = await getIdTokenJwt();
    } catch (e) {
      if (active) setError("Could not load the dashboard: you are not signed in. Refresh the page to sign in again.");
      return;
    }
    const dashId = id ? id :(metbaseReportConfig?.main_dashboard_id === true ? 1:metbaseReportConfig?.main_dashboard_id)
    // Determine the URL based on whether aeronet_api_url is set and not empty
    const url = metbaseReportConfig?.aeronet_api_url?.trim() 
      ? metbaseReportConfig.aeronet_api_url + `/v6/reports/${dashId}`
      : `/v6/metabase/${dashId}`;
    
    if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'local') {
      console.log("Metabase API URL:", url);
    }
    
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: url,
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': token
      },
      maxRedirects: 0
    };
    
    axios.request(config)
    .then((response) => {
      if (active) {
        setError(null)
        setResp(response.data)
        urlTimestampRef.current = Date.now();
        console.log(JSON.stringify(response.data),"response");
      }
    })
    .catch((error) => {
      console.log(error);
      if (active) setError("Could not load the dashboard. Try again, or contact support if it keeps happening.");
    })
  }

  // Only call API after config has been fetched
  if (active && metbaseReportConfig !== undefined && (metbaseReportConfig?.aeronet_api_url?.trim() || axios.defaults.baseURL) && (metbaseReportConfig?.main_dashboard_id || id) ){
    metaBaseReport()
  }

  // Set up interval to refresh URL before expiration
  if (active) {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    refreshIntervalRef.current = setInterval(() => {
      if (active) {
        metaBaseReport();
      }
    }, URL_EXPIRATION_TIME);
  }

  return () => {
    active = false;
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
  }
  },[metbaseReportConfig,id])

  // Show loader until config is fetched, then until report URL is ready
  const configReady = metbaseReportConfig !== undefined;
  const showContent = configReady && resp?.url;

  return (
    <>
    <ListingHeader>Dashboard</ListingHeader>
    {
      !configReady ? (
        <LoadingSpinner />
      ) : error && !showContent ? (
        <Alert severity="error" data-testid="metabase-dashboard-error">{error}</Alert>
      ) : showContent ? (
        <iframe title={resp?.id} style={{
          border: "none"
        }} width={"100%"} height={"100%"} src={resp?.url} />
      ) : (
        <LoadingSpinner />
      )
    }
     
    </>
  );
}


export default MetaBaseDashboard;
